"use client";
import { useHovercardAnchor } from "../hovercard/hovercard-anchor.js";
import { useTooltipProviderContext } from "./tooltip-context.js";
import { createElement, createHook, forwardRef, useEvent } from "@ariakit/react-utils";
import { chain, invariant, isFalsyBooleanCallback } from "@ariakit/utils";
import { useEffect, useRef } from "react";
import { useStoreState } from "@ariakit/react-store";
import { createStore, sync } from "@ariakit/store";
//#region src/tooltip/tooltip-anchor.tsx
const TagName = "div";
const globalStore = createStore({ activeStore: null });
const hidingStores = /* @__PURE__ */ new WeakSet();
function createRemoveStoreCallback(store) {
	return () => {
		const { activeStore } = globalStore.getState();
		if (activeStore !== store) return;
		globalStore.setState("activeStore", null);
	};
}
function hideStore(store) {
	if (!store) return;
	hidingStores.add(store);
	store.hide();
	queueMicrotask(() => hidingStores.delete(store));
}
/**
* Returns props to create a `TooltipAnchor` component.
* @see https://ariakit.com/components/tooltip
* @example
* ```jsx
* const store = useToolTipStore();
* const props = useTooltipAnchor({ store });
* <Role {...props}>Anchor</Role>
* <Tooltip store={store}>Tooltip</Tooltip>
* ```
*/
const useTooltipAnchor = createHook(function useTooltipAnchor({ store, showOnHover = true, ...props }) {
	const context = useTooltipProviderContext();
	store = store || context;
	invariant(store, process.env.NODE_ENV !== "production" && "TooltipAnchor must receive a `store` prop or be wrapped in a TooltipProvider component.");
	const canShowOnHoverRef = useRef(false);
	useEffect(() => {
		return sync(store, ["mounted"], (state) => {
			if (state.mounted) return;
			canShowOnHoverRef.current = false;
		});
	}, [store]);
	useEffect(() => {
		if (!store) return;
		const removeStore = createRemoveStoreCallback(store);
		return chain(removeStore, sync(store, ["mounted"], (state) => {
			if (!store) return;
			if (state.mounted) {
				const { activeStore } = globalStore.getState();
				if (activeStore !== store) {
					if (hidingStores.has(store)) return;
					hideStore(activeStore);
				}
				return globalStore.setState("activeStore", store);
			}
			const id = setTimeout(removeStore, store.getState().skipTimeout);
			return () => clearTimeout(id);
		}));
	}, [store]);
	const onMouseEnterProp = props.onMouseEnter;
	const onMouseEnter = useEvent((event) => {
		onMouseEnterProp?.(event);
		canShowOnHoverRef.current = true;
	});
	const onFocusVisibleProp = props.onFocusVisible;
	const onFocusVisible = useEvent((event) => {
		onFocusVisibleProp?.(event);
		if (event.defaultPrevented) return;
		store?.setAnchorElement(event.currentTarget);
		store?.show();
	});
	const onBlurProp = props.onBlur;
	const onBlur = useEvent((event) => {
		onBlurProp?.(event);
		if (event.defaultPrevented) return;
		const { activeStore } = globalStore.getState();
		canShowOnHoverRef.current = false;
		if (activeStore === store) globalStore.setState("activeStore", null);
	});
	const labelledBy = useStoreState(store, (state) => state.type === "label" ? state.contentElement?.id : void 0);
	props = {
		"aria-labelledby": props["aria-label"] == null ? labelledBy : void 0,
		...props,
		onMouseEnter,
		onFocusVisible,
		onBlur
	};
	props = useHovercardAnchor({
		store,
		showOnHover(event) {
			if (!canShowOnHoverRef.current) return false;
			if (isFalsyBooleanCallback(showOnHover, event)) return false;
			const { activeStore } = globalStore.getState();
			if (!activeStore) return true;
			store?.show();
			return false;
		},
		...props
	});
	return props;
});
/**
* Renders a reference element for a
* [`Tooltip`](https://ariakit.com/reference/tooltip), which is triggered by
* focusing or hovering over the anchor.
*
* The tooltip is strictly for visual purposes. It's your responsibility to
* ensure the anchor element has an accessible name. See [Tooltip anchors must
* have accessible
* names](https://ariakit.com/components/tooltip#tooltip-anchors-must-have-accessible-names)
* @see https://ariakit.com/components/tooltip
* @example
* ```jsx {2}
* <TooltipProvider>
*   <TooltipAnchor>Anchor</TooltipAnchor>
*   <Tooltip>Tooltip</Tooltip>
* </TooltipProvider>
* ```
*/
const TooltipAnchor = forwardRef(function TooltipAnchor(props) {
	return createElement(TagName, useTooltipAnchor(props));
});
//#endregion
export { TooltipAnchor, useTooltipAnchor };

//# sourceMappingURL=tooltip-anchor.js.map